import { ErrorUploadProduct, Product, ProductCreation, ProductCreationWithErrors } from '@Arti-zen/package-backoffice'
import { CircularProgress } from '@mui/material'
import { sendProductsCreationByChunk } from 'modules/product/product.helpers'
import ProductsAfterUploading from 'modules/product/UploadProducts/ProductsAfterUploading'
import TableProducts from 'modules/product/UploadProducts/TableProducts'
import { pages } from 'navigation/pages'
import { useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import styled from 'styled-components'
import { Theme } from 'theme/theme'

type Result = {
  success: boolean
  products: ProductCreation[]
  productsWithErrors?: ProductCreationWithErrors[]
  errorMessage?: string
}

export default function ArtizenUploadProducts() {
  const { id } = useParams()
  const { state } = useLocation() as { state: { result: Result } }

  const [productsWithErrors, setProductsWithErrors] = useState<ProductCreationWithErrors[] | null>(
    state.result.productsWithErrors
  )
  const [products, setProducts] = useState<ProductCreation[] | null>(state.result.products)

  const [message, setMessage] = useState<{ error: boolean; text: string } | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [finalCSV, setFinalCSV] = useState<string | null>(null)
  const [progress, setProgress] = useState<number>(0)

  const onModify = 'id' in (products?.[0] ?? productsWithErrors?.[0] ?? {})
  const uploadingEnded = !!message && message.error === false

  const navigate = useNavigate()

  const onSubmit = async () => {
    setIsLoading(true)
    setMessage(null)

    const res = await sendProductsCreationByChunk({
      productsToSend: products,
      companyId: id,
      onModify,
      onProgress: (processed) => setProgress(processed),
    })

    if (!res.success) {
      setProducts(res.products)
      setProductsWithErrors(res.productsWithErrors)
      if (res.error) {
        setMessage({ error: true, text: getErrorMessage(res.error) })
      }
      setIsLoading(false)
      return
    }

    setFinalCSV(res.csv)
    setIsLoading(false)
    setMessage({ error: false, text: `Les produits ont bien été ${onModify ? 'modifiés' : 'importés'}` })
  }

  const getErrorMessage = (err: ErrorUploadProduct) => {
    switch (err.type) {
      case 'NO_PRODUCTS':
        return 'Aucun produit à importer'
      case 'PRODUCT_WITH_ERRORS':
        return 'Certains des produits à importer contiennent encore des erreurs'
      case 'PROCESSING_ERROR':
        return "Problème lors de l'importation des produits."
    }
  }

  useEffect(() => {
    if (!state) {
      navigate(pages.connected.artisan.upload.navigate(+id))
    }
  }, [state])

  if (isLoading)
    return (
      <LoaderContainer>
        <CircularProgress size={70} color="inherit" />
        <p>{`${progress} produits ${onModify ? 'modifiés' : 'importés'} sur ${products.length}`}</p>
      </LoaderContainer>
    )

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Row>
        <Title>{onModify ? 'Modifier' : 'Importer'} les produits</Title>
      </Row>

      {message && <p style={{ color: message?.error ? 'red' : '#19d692' }}>{message.text}</p>}

      {uploadingEnded ? (
        <ProductsAfterUploading csv={finalCSV} products={products as Product[]} onModify={onModify} />
      ) : (
        <TableProducts
          products={products}
          setProducts={setProducts}
          productsWithErrors={productsWithErrors}
          setProductsWithErrors={setProductsWithErrors}
          onSubmit={onSubmit}
          onModify={onModify}
        />
      )}
    </div>
  )
}

const Row = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 15px;
`
export const Title = styled.p`
  font-family: ${Theme.font.family};
  font-size: 15px;
  font-weight: 600;
  color: ${Theme.colors.blackArtizen};
`

const LoaderContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
  justify-content: center;
  align-items: center;
  color: ${Theme.colors.blueArtizen};
  margin-top: -100px;
`
